import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiEye, FiEyeOff } from 'react-icons/fi';
import { useFormik } from 'formik';
import { useAuthStore, useRateStore, useResourceStore } from '../../store';

import StepTimeline from '../../components/base/step-timeline';
import PhoneNumberInput from '../../components/base/phone-input-tel';
import GlobalIcon from '../../assets/components/global';
import CustomDropdown from '../../components/base/custom-dropdown';
import AppInput from '../../components/base/input';
import AppButton from '../../components/base/button';
import signupValidationSchemas from '../../constants/schemas/sign-up';
import AuthFooter from '../../layout/component/auth-footer';
import toast from 'react-hot-toast';
import { isValidPassword } from '../../utils/resources.util';
import Loader from '../../components/base/loader';
import Button from '../../components/base/button';
import { emailRegex } from '../../constants/constant';
import BaseRadioGroupNew from '../../components/base/radio-group-new';
import { AccountTypeEnum } from '../../enums/account.enum';

const Signup: React.FC = () => {
  const steps = ['Personal Info', 'Account Type', 'Account Security'];
  const [currentStep, setCurrentStep] = useState(1); // Set the current step (0-based index)
  const [touched, setTouched] = useState(false);
  const [mobileNumberIsOkay, setMobileNumberIsOkay] = useState(false);
  const [mobileNumberIsTouched, setMobileNumberIsTouched] = useState(false);

  const navigate = useNavigate();

  const handleCountryChange = (value: string) => {
    formik.values.countryIso3 = value;
    setMobileNumberIsTouched(true);
  };

  const handlePhoneChange = (value: string) => {
    formik.values.mobileNumber = value;
  };

  const routeToLogIn = () => {
    navigate('/login');
  };

  const routeToSuccessPage = () => {
    navigate('/success');
  };

  const [showPassword, setShowPassword] = React.useState(false);
  const [accountType, setAccountType] = React.useState(1);

  const { signup, setSignedUpEmail, loading, validateEmail } = useAuthStore();
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      businessName: '',
      email: '',
      password: '',
      mobileNumber: '',
      countryIso3: '',
      countryName: '',
      accountType: 1,
    },
    validationSchema: signupValidationSchemas[currentStep - 1],
    validateOnMount: true,
    async onSubmit(values) {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      } else if (currentStep === 3) {
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email.trim(),
          password: values.password,
          mobileNumber: values.mobileNumber,
          countryIso3: values.countryIso3,
          accountType: accountType,
          businessName: values.businessName,
        };

        const response = await signup(payload);
        if (response.isSuccess) {
          setSignedUpEmail(values.email);
          toast.success(response.value);
          routeToSuccessPage();
        }
      }
    },
  });

  const [validateEmailLoading, setValidateEmailLoading] = useState(false);
  const [emailOkay, setEmailOkay] = useState(false);
  const validateEmailHandler = async (email: string) => {
    if (!email) {
      return;
    }
    if (!emailRegex.test(email)) {
      toast.error(`"${email}" is not a valid email`);
      return;
    }
    setValidateEmailLoading(true);
    const response = await validateEmail(email);
    if (response && response.isSuccess) {
      toast.success('Email is available.');
      setEmailOkay(true);
    } else {
      setEmailOkay(false);
    }
    setValidateEmailLoading(false);
  };

  const { getActiveCountries, activeCountries } = useRateStore();
  const filteredCountries = activeCountries.filter(
    (country) =>
      country.name === 'Nigeria' ||
      country.name === 'Ghana' ||
      country.name === 'Senegal' ||
      country.name === 'Cameroon',
  );
  useEffect(() => {
    getActiveCountries();
  }, []);

  return (
    <>
      <div className="2xl:p-6 px-6 py-2 w-[100%] md:w-[70%] mx-auto mb-4 flex flex-col justify-center md:min-h-screen relative animate__animated animate__slideInLeft">
        <div className="mb-6">
          <p className="font-bold mb-4 2xl:text-4xl text-2xl w-[90%]">
            Get Started for Free Today
          </p>
          <p className="text-lg">Get started in 3 simple steps.</p>
        </div>

        <div className="mb-4">
          <StepTimeline steps={steps} currentStep={currentStep} />
        </div>

        <form onSubmit={formik.handleSubmit} action="#">
          {/* Step 1 */}
          {currentStep === 1 && (
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <BaseRadioGroupNew
                  label="Account Type"
                  required
                  name={`accountType-${Date.now()}`}
                  options={[
                    // {
                    //   label: 'Individual',
                    //   value: AccountTypeEnum.PersonalAccount,
                    // },
                    {
                      label: 'Business',
                      value: AccountTypeEnum.BusinessAccount,
                    },
                  ]}
                  value={accountType}
                  onChange={(value) => setAccountType(Number(value))}
                />
              </div>

              <div>
                <AppInput
                  type="text"
                  name="firstName"
                  id="firstName"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                  placeholder="Enter your first name"
                  required
                  label="First Name"
                  errorMessage={
                    formik.touched.firstName && formik.errors.firstName
                      ? formik.errors.firstName
                      : ''
                  }
                />
              </div>

              <div>
                <AppInput
                  type="text"
                  name="lastName"
                  id="lastName"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                  placeholder="Enter your last name"
                  required
                  label="Last Name"
                  errorMessage={
                    formik.touched.lastName && formik.errors.lastName
                      ? formik.errors.lastName
                      : ''
                  }
                />
              </div>

              {!!accountType && (
                <div>
                  <AppInput
                    type="text"
                    name="businessName"
                    id="businessName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.businessName}
                    placeholder="Enter your business name"
                    required
                    label="Business Name"
                    errorMessage={
                      formik.touched.businessName && formik.errors.businessName
                        ? formik.errors.businessName
                        : ''
                    }
                  />
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <AppInput
                  type="text"
                  name="email"
                  id="email"
                  onChange={(e) => {
                    formik.handleChange(e);
                    setEmailOkay(false);
                  }}
                  value={formik.values.email}
                  placeholder="Enter your email address"
                  required
                  label="Email Address"
                  endIcon={
                    validateEmailLoading ? (
                      <Loader
                        width="1.2rem"
                        borderTopColor="border-t-primary"
                      />
                    ) : emailOkay ? (
                      <FiCheck />
                    ) : (
                      <button
                        type="button"
                        className={`px-2 py-1 text-sm font-light text-white bg-black whitespace-pre rounded-lg`}
                        onClick={() => {
                          validateEmailHandler(formik.values.email);
                        }}
                      >
                        Validate
                      </button>
                    )
                  }
                  errorMessage={
                    formik.touched.email && formik.errors.email
                      ? formik.errors.email
                      : ''
                  }
                />
              </div>

              <div>
                <AppInput
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  placeholder="Enter your password"
                  required
                  label="Password"
                  endIcon={
                    <button
                      type="button"
                      className={`absolute px-4 right-0`}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEye /> : <FiEyeOff />}
                    </button>
                  }
                  errorMessage={
                    formik.touched.password && formik.errors.password
                      ? formik.errors.password
                      : ''
                  }
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <CustomDropdown
                  icon={<GlobalIcon />}
                  label={'Select Country of Registration'}
                  options={filteredCountries.map((country) => {
                    return {
                      name: country.iso3,
                      label: country.name,
                      countryFlag: country.flagUrl,
                    };
                  })}
                  emitObject
                  selectedValue={formik.values.countryIso3}
                  onSelectChange={(value) => {
                    formik.values.countryName = value.label;
                    handleCountryChange(value.name);
                    setTouched(true);
                  }}
                  required
                  showCountryFlag
                  errorMessage={
                    !formik.values.countryIso3 && touched
                      ? 'Country is required'
                      : ''
                  }
                />
              </div>

              <div>
                <PhoneNumberInput
                  label="Phone Number"
                  phone={formik.values.mobileNumber}
                  onPhoneChange={handlePhoneChange}
                  onPhoneValidCheck={(valid) => setMobileNumberIsOkay(valid)}
                  required
                  errorMessage={
                    (mobileNumberIsTouched && !formik.values.mobileNumber) ||
                    (mobileNumberIsTouched &&
                      formik.values.mobileNumber &&
                      !mobileNumberIsOkay)
                      ? 'Please enter a valid phone number'
                      : ''
                  }
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-6">
            {currentStep > 1 && (
              <AppButton
                label="Previous"
                type="light"
                clicked={() => setCurrentStep(currentStep - 1)}
              />
            )}
            {currentStep < 3 && (
              <AppButton
                label={
                  currentStep === 1
                    ? 'Proceed to Step 2'
                    : 'Proceed to Final Step'
                }
                type="dark"
                disabled={
                  currentStep === 1
                    ? !formik.values.firstName ||
                      !formik.values.businessName ||
                      !formik.values.lastName
                    : !formik.values.email ||
                      !emailOkay ||
                      !formik.values.password ||
                      !isValidPassword(formik.values.password)
                }
                clicked={() => setCurrentStep(currentStep + 1)}
              />
            )}
            {currentStep === 3 && (
              <AppButton
                label="Create Account"
                submitType="submit"
                type="dark"
                loading={loading}
                disabled={
                  loading || !formik.values.countryIso3 || !mobileNumberIsOkay
                }
              />
            )}
          </div>
        </form>

        <p className="mt-6 text-sm text-center">
          Already have an account?{' '}
          <span
            onClick={routeToLogIn}
            className="text-primary font-bold cursor-pointer inline-block ml-1"
          >
            Login Here
          </span>
        </p>

        <AuthFooter />
      </div>
    </>
  );
};

export default Signup;
